import React, { useRef, useState, useEffect } from 'react';
import { PlanType, ToolRun, Tool } from '../types';
import ResultRenderer from './ResultRenderer';
import registryData from '../data/registries/marketxtractor_render_block_registry_v_1.json';

interface ResultViewerProps {
  run: ToolRun;
  tool?: Tool;
  userPlan?: PlanType;
  onRunAgain?: () => void;
}

const ResultViewer: React.FC<ResultViewerProps> = ({ run, tool, userPlan, onRunAgain }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    if (isFullscreen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    } else {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);

  const ResultContent = () => (
    <div className="p-10 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 overflow-auto shadow-inner flex-1 selection:bg-indigo-100 dark:selection:bg-indigo-900/40">
      <div className="max-w-4xl mx-auto">
        {run.responseEnvelope || (run.renderBlocks && run.renderBlocks.length > 0) ? (
          <ResultRenderer 
            data={run.responseEnvelope?.data || run.normalizedOutput || {}}
            outputSpec={run.responseEnvelope?.outputSpec || run.responseEnvelope?.output_spec_json || run.responseEnvelope || {}}
            renderMap={run.responseEnvelope?.renderMap || run.responseEnvelope?.render_map || undefined}
            registry={registryData as any}
            templateId={(run.responseEnvelope?.meta?.result_template_id || tool?.resultTemplateId) as any}
            userPlan={userPlan || run.responseEnvelope?.meta?.user_plan}
            responseMode={
              run.responseEnvelope?.meta?.response_mode ||
              run.responseEnvelope?.meta?.request_context?.response_mode ||
              run.responseEnvelope?.data?.response_mode
            }
            theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
            toolContext={{
              toolId: tool?.catalogId || tool?.id,
              toolName: tool?.name,
              iconEmoji: tool?.icon,
              category: tool?.category || tool?.subCategory
            }}
          />
        ) : (
          <div
            ref={containerRef}
            className="prose prose-slate dark:prose-invert max-w-none prose-sm sm:prose-base font-sans"
            dangerouslySetInnerHTML={{ __html: run.htmlContent }}
          />
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-3xl overflow-hidden shadow-2xl transition-colors flex flex-col h-full">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex flex-col xl:flex-row xl:items-center justify-between gap-4 bg-slate-50/80 dark:bg-slate-900/50 backdrop-blur-sm shrink-0">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white font-bold shadow-lg">
              📜
            </div>
            <div>
              <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">ID: {run.id.split('-')[0]}</span>
              <div className="text-sm font-black text-slate-900 dark:text-white">{new Date(run.timestamp).toLocaleString()}</div>
            </div>
            <span className="px-2.5 py-1 bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-500 text-[10px] font-black rounded-full border border-emerald-500/20 uppercase tracking-widest">
              {run.status}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={toggleFullscreen}
              className="p-2.5 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-xl text-slate-500 dark:text-slate-400 transition-colors shadow-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800"
              title="Fullscreen"
            >
              ⛶
            </button>
            <button
              onClick={onRunAgain}
              className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-black rounded-xl transition-all shadow-xl shadow-indigo-600/30 tracking-widest uppercase"
            >
              Run Again
            </button>
          </div>
        </div>

        <ResultContent />

        <div className="px-8 py-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shrink-0">
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-[10px] uppercase font-black tracking-widest text-slate-500 dark:text-slate-400">
            <div><span className="text-slate-400">CREDIT CONSUMPTION:</span> {run.creditsSpent}</div>
            <div><span className="text-slate-400">ENGINE ID:</span> {run.toolId}</div>
            {run.normalizedOutput && <div><span className="text-emerald-500 font-bold tracking-tight">DATA EXPORT READY</span></div>}
          </div>
          <div className="text-[10px] text-slate-400 dark:text-slate-500 font-bold tracking-widest uppercase">
            MarketXtractor ShadowEngine v1.6
          </div>
        </div>
      </div>

      {/* Fullscreen Overlay */}
      {isFullscreen && (
        <div className="fixed inset-0 z-[999] bg-white dark:bg-slate-950 flex flex-col transition-all">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 backdrop-blur-md">
            <div className="flex items-center space-x-4">
              <span className="font-black text-mx-text-primary tracking-tight">Full Report Viewer</span>
              <span className="text-xs text-mx-text-muted opacity-50 font-bold uppercase tracking-widest">{run.toolId}</span>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={toggleFullscreen}
                className="px-6 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-xs font-black rounded-xl transition-all shadow-lg hover:scale-105 active:scale-95 uppercase tracking-widest"
              >
                Close (Esc)
              </button>
            </div>
          </div>
          <ResultContent />
        </div>
      )}
    </>
  );
};

export default ResultViewer;
